import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Navbar } from "@/components/Navbar";
import { Plus, Trash2, Pencil, Users, Send, Loader2, BookOpen, Calendar as CalendarIcon, Mail, MailOpen, AlertCircle, CheckCircle2, PenLine, Settings as SettingsIcon, Download, Gift } from "lucide-react";
import { toast } from "sonner";
import { DEFAULT_SETTINGS, clearSettingsCache } from "@/lib/settings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuth = () => {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : null;
};

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [trialRequests, setTrialRequests] = useState([]);
  const [members, setMembers] = useState([]);
  const [memberQuery, setMemberQuery] = useState("");
  const [orders, setOrders] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [orderQuery, setOrderQuery] = useState("");
  const [confirmAsk, setConfirmAsk] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null);
  const [reclaimCredits, setReclaimCredits] = useState(true);
  const [refunding, setRefunding] = useState(false);
  const [posts, setPosts] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [termInput, setTermInput] = useState("");
  const [substackUrl, setSubstackUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [postDialog, setPostDialog] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [memberDialog, setMemberDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => localStorage.getItem("tm_admin_tab") || "classes");

  // Dialog states
  const [classDialog, setClassDialog] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [subjectDialog, setSubjectDialog] = useState(false);
  const [bookingsDialog, setBookingsDialog] = useState(null);
  const [classBookings, setClassBookings] = useState([]);

  useEffect(() => {
    const stored = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    if (!stored || !token) { navigate("/admin/login"); return; }
    setUser(JSON.parse(stored));
    fetchData();
  }, [navigate]);

  const fetchData = useCallback(async () => {
    const auth = getAuth();
    if (!auth) return;
    // Don't flip back to the full-page loader on refreshes — it remounts the tabs
    // and throws the admin back to Classes after every action.
    try {
      const [clsRes, subRes, bookRes, msgRes, trialRes, memberRes, blogRes, setRes, revRes] = await Promise.all([
        axios.get(`${API}/classes/all`, auth),
        axios.get(`${API}/subjects`),
        axios.get(`${API}/bookings`, auth),
        axios.get(`${API}/admin/contact-messages`, auth),
        axios.get(`${API}/admin/trial-requests`, auth),
        axios.get(`${API}/admin/members`, auth),
        axios.get(`${API}/blog?include_drafts=true`, auth),
        axios.get(`${API}/settings`),
        axios.get(`${API}/admin/revenue`, auth),
      ]);
      setClasses(clsRes.data);
      setSubjects(subRes.data);
      setBookings(bookRes.data);
      setMessages(msgRes.data);
      setTrialRequests(trialRes.data);
      setMembers(memberRes.data);
      setPosts(blogRes.data);
      setSettings(setRes.data);
      setTermInput(setRes.data.term_label || "");
      setRevenue(revRes.data);
      reloadOrders();
    } catch (err) {
      if (err.response?.status === 401) { handleLogout(); return; }
      toast.error("Failed to load data");
    } finally { setLoading(false); }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/admin/login");
  };

  const handleSendReminders = async () => {
    try {
      const res = await axios.post(`${API}/admin/send-reminders`, {}, getAuth());
      toast.success(res.data.message);
    } catch { toast.error("Failed to send reminders"); }
  };

  const saveSettings = async (fields) => {
    try {
      const res = await axios.put(`${API}/admin/settings`, fields, getAuth());
      setSettings(res.data);
      clearSettingsCache();
      toast.success("Site settings updated");
    } catch { toast.error("Could not save settings"); }
  };

  const reloadOrders = async () => {
    try {
      const res = await axios.get(`${API}/admin/orders`, getAuth());
      setOrders(res.data);
    } catch { toast.error("Could not load orders"); }
  };

  /** Styled replacement for window.confirm — opens an in-app dialog and runs `action` on confirm. */
  const askConfirm = (title, description, action, actionLabel = "Delete") =>
    setConfirmAsk({ title, description, action, actionLabel });

  const runConfirm = async () => {
    if (!confirmAsk) return;
    setConfirmBusy(true);
    try {
      await confirmAsk.action();
      setConfirmAsk(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "That didn't work — please try again");
    } finally {
      setConfirmBusy(false);
    }
  };

  const refundOrder = (o) => {
    setRefundTarget(o);
    setReclaimCredits(true);
  };

  const confirmRefund = async () => {
    const o = refundTarget;
    if (!o) return;
    setRefunding(true);
    try {
      const res = await axios.post(`${API}/admin/orders/${o.id}/refund`, { reclaim_credits: reclaimCredits }, getAuth());
      toast.success(`Refunded. Stripe id ${res.data.refund_id || "n/a"}`);
      setRefundTarget(null);
      reloadOrders(); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Refund failed"); }
    finally { setRefunding(false); }
  };

  const reloadBlog = async () => {
    const res = await axios.get(`${API}/blog?include_drafts=true`);
    setPosts(res.data);
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar isAdmin onLogout={handleLogout} />
        <div className="pt-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-purple" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0f1117] transition-colors duration-300" data-testid="admin-dashboard">
      <Navbar isAdmin onLogout={handleLogout} />
      <div className="pt-20 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">Dashboard</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 font-body">Welcome, {user.name}</p>
            </div>
            <Button onClick={handleSendReminders} variant="outline" className="rounded-full border-brand-purple text-brand-purple hover:bg-brand-purple-soft dark:border-brand-purple-light dark:text-brand-purple-light dark:hover:bg-brand-purple/20 font-body" data-testid="send-reminders-btn">
              <Send className="h-4 w-4 mr-2" /> Send Reminders
            </Button>
          </div>

          {/* Revenue snapshot — this month */}
          <div className="mb-6 rounded-2xl bg-brand-purple text-white p-5 sm:p-6 shadow-sm" data-testid="revenue-snapshot">
            <div className="flex items-baseline justify-between gap-4 mb-4">
              <h2 className="font-heading text-lg font-bold">This month</h2>
              <span className="text-xs font-body text-white/70" data-testid="revenue-month">
                {revenue?.month ? new Date(`${revenue.month}-01T00:00:00Z`).toLocaleDateString("en-AU", { month: "long", year: "numeric", timeZone: "UTC" }) : "—"}
              </span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Net income", value: `$${(revenue?.net_income ?? 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, testid: "revenue-net-income", sub: `$${(revenue?.gross_income ?? 0).toFixed(2)} taken in` },
                { label: "Credits sold", value: revenue?.credits_sold ?? 0, testid: "revenue-credits-sold", sub: `${revenue?.paid_orders ?? 0} paid order${(revenue?.paid_orders ?? 0) === 1 ? "" : "s"}` },
                { label: "Refunded", value: `$${(revenue?.refunded_amount ?? 0).toFixed(2)}`, testid: "revenue-refunded", sub: `${revenue?.refunds ?? 0} refund${(revenue?.refunds ?? 0) === 1 ? "" : "s"}` },
                { label: "Awaiting payment", value: revenue?.pending_orders ?? 0, testid: "revenue-pending", sub: "checkouts not completed" },
              ].map((s) => (
                <div key={s.testid} className="rounded-xl bg-white/10 p-4" data-testid={s.testid}>
                  <p className="font-heading text-2xl font-bold leading-tight">{s.value}</p>
                  <p className="text-xs font-body text-white/90 mt-1">{s.label}</p>
                  <p className="text-[11px] font-body text-white/60">{s.sub}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { label: "Total Classes", value: classes.length, icon: CalendarIcon },
              { label: "Active Classes", value: classes.filter(c => c.status === "active").length, icon: BookOpen },
              { label: "Subjects", value: subjects.length, icon: BookOpen },
              { label: "Paid Bookings", value: bookings.filter(b => b.payment_status === "paid").length, icon: Users },
            ].map((stat, i) => (
              <div key={i} className="bg-white dark:bg-[#1a1b23] rounded-xl border border-slate-100 dark:border-slate-800 p-4 shadow-sm transition-colors duration-300">
                <stat.icon className="h-5 w-5 text-brand-purple mb-2" />
                <p className="font-heading text-2xl font-bold text-slate-900 dark:text-white">{stat.value}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-body">{stat.label}</p>
              </div>
            ))}
          </div>

          <Tabs value={tab} onValueChange={(v) => { setTab(v); localStorage.setItem("tm_admin_tab", v); }} className="space-y-6">
            <TabsList className="bg-white dark:bg-[#1a1b23] border border-slate-100 dark:border-slate-800 rounded-full p-1">
              <TabsTrigger value="classes" className="rounded-full font-body text-sm data-[state=active]:bg-brand-purple data-[state=active]:text-white" data-testid="tab-classes">Classes</TabsTrigger>
              <TabsTrigger value="subjects" className="rounded-full font-body text-sm data-[state=active]:bg-brand-purple data-[state=active]:text-white" data-testid="tab-subjects">Subjects</TabsTrigger>
              <TabsTrigger value="bookings" className="rounded-full font-body text-sm data-[state=active]:bg-brand-purple data-[state=active]:text-white" data-testid="tab-bookings">Bookings</TabsTrigger>
              <TabsTrigger value="messages" className="rounded-full font-body text-sm data-[state=active]:bg-brand-purple data-[state=active]:text-white relative" data-testid="tab-messages">
                Messages
                {messages.filter(m => !m.read).length > 0 && (
                  <span className="ml-2 bg-brand-gold text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-testid="unread-count">
                    {messages.filter(m => !m.read).length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="trials" className="rounded-full font-body text-sm data-[state=active]:bg-brand-purple data-[state=active]:text-white relative" data-testid="tab-trials">
                Enrolments
                {trialRequests.filter(t => t.status === "new").length > 0 && (
                  <span className="ml-2 bg-brand-gold text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full" data-testid="new-trials-count">
                    {trialRequests.filter(t => t.status === "new").length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="members" className="rounded-full font-body text-sm data-[state=active]:bg-brand-purple data-[state=active]:text-white" data-testid="tab-members">
                Members
                {members.length > 0 && (
                  <span className="ml-2 bg-slate-200 text-slate-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{members.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="orders" className="rounded-full font-body text-sm data-[state=active]:bg-brand-purple data-[state=active]:text-white" data-testid="tab-orders">
                Orders
                {orders.length > 0 && (
                  <span className="ml-2 bg-slate-200 text-slate-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{orders.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="blog" className="rounded-full font-body text-sm data-[state=active]:bg-brand-purple data-[state=active]:text-white" data-testid="tab-blog">
                Blog
                {posts.length > 0 && (
                  <span className="ml-2 bg-slate-200 text-slate-700 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{posts.length}</span>
                )}
              </TabsTrigger>
              <TabsTrigger value="settings" className="rounded-full font-body text-sm data-[state=active]:bg-brand-purple data-[state=active]:text-white" data-testid="tab-settings">Settings</TabsTrigger>
            </TabsList>

            {/* Classes Tab */}
            <TabsContent value="classes" data-testid="classes-tab-content">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">Manage Classes</h2>
                <Button onClick={() => { setEditingClass(null); setClassDialog(true); }} className="bg-brand-purple hover:bg-brand-purple-hover text-white rounded-full font-body" data-testid="add-class-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Class
                </Button>
              </div>
              <div className="space-y-3">
                {classes.length === 0 ? (
                  <div className="text-center py-12 bg-white dark:bg-[#1a1b23] rounded-xl border border-slate-100 dark:border-slate-800 transition-colors duration-300">
                    <CalendarIcon className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 font-body">No classes yet. Add your first class!</p>
                  </div>
                ) : classes.map((cls) => (
                  <AdminClassCard key={cls.id} cls={cls} onEdit={() => { setEditingClass(cls); setClassDialog(true); }}
                    onDelete={() => askConfirm(
                      "Delete this class?",
                      `${cls.title} — ${cls.date}. Any bookings on it stay in your records, but the class disappears from the timetable.`,
                      async () => {
                        await axios.delete(`${API}/classes/${cls.id}`, getAuth());
                        toast.success("Class deleted"); fetchData();
                      })}
                    onViewBookings={async () => {
                      const res = await axios.get(`${API}/bookings/class/${cls.id}`, getAuth());
                      setClassBookings(res.data); setBookingsDialog(cls);
                    }} />
                ))}
              </div>
            </TabsContent>

            {/* Subjects Tab */}
            <TabsContent value="subjects" data-testid="subjects-tab-content">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">Manage Subjects</h2>
                <Button onClick={() => setSubjectDialog(true)} className="bg-brand-purple hover:bg-brand-purple-hover text-white rounded-full font-body" data-testid="add-subject-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Subject
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {subjects.map((s) => (
                  <div key={s.id} className="bg-white dark:bg-[#1a1b23] rounded-xl border border-slate-100 dark:border-slate-800 p-5 flex justify-between items-start transition-colors duration-300" data-testid={`subject-card-${s.id}`}>
                    <div>
                      <h3 className="font-heading font-semibold text-slate-900 dark:text-white">{s.name}</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400 font-body">{s.description || "No description"}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-600 hover:bg-red-50" data-testid={`delete-subject-${s.id}`}
                      onClick={() => askConfirm(
                        `Delete "${s.name}"?`,
                        "Classes already using this subject are not changed.",
                        async () => {
                          await axios.delete(`${API}/subjects/${s.id}`, getAuth());
                          toast.success("Subject deleted"); fetchData();
                        })}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Bookings Tab */}
            <TabsContent value="bookings" data-testid="bookings-tab-content">
              <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-white mb-4">All Bookings</h2>
              <div className="bg-white dark:bg-[#1a1b23] rounded-xl border border-slate-100 dark:border-slate-800 overflow-hidden transition-colors duration-300">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm font-body">
                    <thead className="bg-slate-50 dark:bg-[#13141c] border-b border-slate-100 dark:border-slate-800">
                      <tr>
                        <th className="text-left px-4 py-3 text-slate-600 font-semibold">Student</th>
                        <th className="text-left px-4 py-3 text-slate-600 font-semibold">Email</th>
                        <th className="text-left px-4 py-3 text-slate-600 font-semibold">Class</th>
                        <th className="text-left px-4 py-3 text-slate-600 font-semibold">Status</th>
                        <th className="text-left px-4 py-3 text-slate-600 font-semibold">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.length === 0 ? (
                        <tr><td colSpan={5} className="text-center py-8 text-slate-400">No bookings yet</td></tr>
                      ) : bookings.map((b) => {
                        const cls = classes.find(c => c.id === b.class_id);
                        return (
                          <tr key={b.id} className="border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-[#13141c]" data-testid={`booking-row-${b.id}`}>
                            <td className="px-4 py-3 text-slate-900 dark:text-white">{b.student_name}</td>
                            <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{b.student_email}</td>
                            <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{cls?.title || b.class_id}</td>
                            <td className="px-4 py-3">
                              <Badge className={b.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                                {b.payment_status}
                              </Badge>
                            </td>
                            <td className="px-4 py-3 text-slate-500">{b.created_at?.split("T")[0]}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Messages Tab */}
            <TabsContent value="messages" data-testid="messages-tab-content">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">Contact Form Messages</h2>
                <span className="text-xs text-slate-500 font-body">{messages.length} total · {messages.filter(m => !m.read).length} unread</span>
              </div>

              {messages.some(m => !m.email_sent) && (
                <div className="mb-4 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl p-4 flex gap-3" data-testid="email-warning">
                  <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm font-body">
                    <p className="font-semibold text-amber-900 dark:text-amber-300">Email delivery blocked</p>
                    <p className="text-amber-800 dark:text-amber-400">Some submissions weren't emailed because the Resend domain isn't verified. All messages are saved here — verify your domain at <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline font-semibold">resend.com/domains</a> to enable email alerts.</p>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {messages.length === 0 ? (
                  <div className="text-center py-12 bg-white dark:bg-[#1a1b23] rounded-xl border border-slate-100 dark:border-slate-800" data-testid="no-messages-state">
                    <Mail className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 font-body">No contact messages yet.</p>
                  </div>
                ) : messages.map((m) => (
                  <div key={m.id} className={`bg-white dark:bg-[#1a1b23] rounded-xl border p-5 transition-colors ${m.read ? "border-slate-100 dark:border-slate-800" : "border-brand-purple/30 dark:border-brand-purple-light/30 ring-1 ring-brand-purple/10"}`} data-testid={`message-card-${m.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {m.read ? <MailOpen className="h-4 w-4 text-slate-400" /> : <Mail className="h-4 w-4 text-brand-purple" />}
                          <h3 className="font-heading font-semibold text-slate-900 dark:text-white">{m.name}</h3>
                          <Badge className="bg-brand-purple-soft text-brand-purple font-body text-xs rounded-full">{m.subject}</Badge>
                          {!m.read && <Badge className="bg-brand-gold text-white font-body text-xs rounded-full">NEW</Badge>}
                          {m.email_sent ? (
                            <span className="text-xs text-green-600 flex items-center gap-1 font-body"><CheckCircle2 className="h-3 w-3" /> Emailed</span>
                          ) : (
                            <span className="text-xs text-amber-600 flex items-center gap-1 font-body"><AlertCircle className="h-3 w-3" /> Not emailed</span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 font-body">
                          <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject)}`} className="text-brand-purple hover:underline font-semibold" data-testid={`reply-email-${m.id}`}>{m.email}</a>
                          {m.phone && <a href={`tel:${m.phone}`} className="hover:text-brand-purple">{m.phone}</a>}
                          <span>{new Date(m.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!m.read && (
                          <Button size="sm" variant="outline" className="rounded-full font-body text-xs" data-testid={`mark-read-${m.id}`}
                            onClick={async () => {
                              await axios.post(`${API}/admin/contact-messages/${m.id}/read`, {}, getAuth());
                              fetchData();
                            }}>
                            Mark read
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full" data-testid={`delete-message-${m.id}`}
                          onClick={() => askConfirm(
                            "Delete this enquiry?",
                            `From ${m.name} (${m.email}). This can't be undone.`,
                            async () => {
                              await axios.delete(`${API}/admin/contact-messages/${m.id}`, getAuth());
                              toast.success("Message deleted"); fetchData();
                            })}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="bg-slate-50 dark:bg-[#13141c] rounded-lg p-3">
                      <p className="text-sm text-slate-700 dark:text-slate-300 font-body whitespace-pre-wrap leading-relaxed">{m.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Trials Tab */}
            <TabsContent value="trials" data-testid="trials-tab-content">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">Enrolment Enquiries</h2>
                <span className="text-xs text-slate-500 font-body">{trialRequests.length} total · {trialRequests.filter(t => t.status === "new").length} new</span>
              </div>

              <div className="space-y-3">
                {trialRequests.length === 0 ? (
                  <div className="text-center py-12 bg-white dark:bg-[#1a1b23] rounded-xl border border-slate-100 dark:border-slate-800" data-testid="no-trials-state">
                    <Mail className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 font-body">No trial requests yet.</p>
                  </div>
                ) : trialRequests.map((t) => (
                  <div key={t.id} className={`bg-white dark:bg-[#1a1b23] rounded-xl border p-5 ${t.status === "new" ? "border-brand-gold/40 ring-1 ring-brand-gold/10" : "border-slate-100 dark:border-slate-800"}`} data-testid={`trial-card-${t.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-heading font-semibold text-slate-900 dark:text-white">{t.student_name}</h3>
                          <span className="text-xs text-slate-500 font-body">· parent: <strong>{t.parent_name}</strong></span>
                          <Badge className={`font-body text-xs rounded-full ${
                            t.status === "new" ? "bg-brand-gold text-white" :
                            t.status === "contacted" ? "bg-blue-100 text-blue-700" :
                            t.status === "converted" ? "bg-green-100 text-green-700" :
                            "bg-slate-100 text-slate-700"
                          }`}>{t.status.toUpperCase()}</Badge>
                          {t.email_sent ? (
                            <span className="text-xs text-green-600 flex items-center gap-1 font-body" data-testid={`trial-emailed-${t.id}`}>
                              <CheckCircle2 className="h-3 w-3" /> Emailed to you
                            </span>
                          ) : (
                            <span className="text-xs text-amber-600 flex items-center gap-1 font-body"
                              title={t.email_error || "The notification email did not send"}
                              data-testid={`trial-not-emailed-${t.id}`}>
                              <AlertCircle className="h-3 w-3" /> Alert email failed
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 font-body mb-2">
                          <a href={`tel:${t.parent_phone}`} className="text-brand-purple font-semibold hover:underline">📞 {t.parent_phone}</a>
                          {t.student_email && <a href={`mailto:${t.student_email}`} className="text-brand-purple hover:underline">{t.student_email}</a>}
                          {t.student_phone && <span>Student: {t.student_phone}</span>}
                          <span>{new Date(t.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</span>
                        </div>
                        {t.subjects_interested && <p className="text-sm text-brand-gold font-body font-semibold mb-1">Subjects: {t.subjects_interested}</p>}
                        {t.texts_studying && <p className="text-sm text-slate-600 dark:text-slate-300 font-body mb-1">Texts this year: {t.texts_studying}</p>}
                        {t.reason && <p className="text-sm text-slate-600 dark:text-slate-300 font-body whitespace-pre-wrap bg-slate-50 dark:bg-[#13141c] rounded-lg p-3 mt-2">Why tutoring: {t.reason}</p>}
                        {t.trial_offered && <Badge className="bg-brand-gold/10 text-brand-gold font-body text-xs rounded-full mt-2">FREE TRIAL OFFERED</Badge>}
                        {t.notes && <p className="text-sm text-slate-600 dark:text-slate-300 font-body whitespace-pre-wrap bg-slate-50 dark:bg-[#13141c] rounded-lg p-3 mt-2">{t.notes}</p>}
                      </div>
                      <div className="flex flex-col gap-2 min-w-[140px]">
                        <select
                          value={t.status}
                          onChange={async (e) => {
                            await axios.post(`${API}/admin/trial-requests/${t.id}/status`, { status: e.target.value }, getAuth());
                            toast.success("Status updated"); fetchData();
                          }}
                          className="text-xs font-body rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#13141c] px-2 py-1.5"
                          data-testid={`trial-status-${t.id}`}>
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="converted">Converted</option>
                          <option value="declined">Declined</option>
                        </select>
                        <Button variant="outline" size="sm" className="rounded-full font-body text-xs" data-testid={`offer-trial-${t.id}`}
                          onClick={async () => {
                            await axios.post(`${API}/admin/trial-requests/${t.id}/offer-trial`, { offered: !t.trial_offered }, getAuth());
                            toast.success(t.trial_offered ? "Trial offer removed" : "Marked as free trial offered");
                            fetchData();
                          }}>
                          <Gift className="h-3.5 w-3.5 mr-1" /> {t.trial_offered ? "Undo trial" : "Offer free trial"}
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg text-xs" data-testid={`delete-trial-${t.id}`}
                          onClick={() => askConfirm(
                            "Delete this enrolment enquiry?",
                            `${t.student_name || t.parent_name} — ${t.parent_phone}. This can't be undone.`,
                            async () => {
                              await axios.delete(`${API}/admin/trial-requests/${t.id}`, getAuth());
                              toast.success("Deleted"); fetchData();
                            })}>
                          <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Members Tab */}
            <TabsContent value="members" data-testid="members-tab-content">
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">Members</h2>
                <Button onClick={() => setMemberDialog(true)} className="bg-brand-purple hover:bg-brand-purple-hover text-white rounded-full font-body" data-testid="add-member-btn">
                  <Plus className="h-4 w-4 mr-1" /> New Member
                </Button>
              </div>

              <Input value={memberQuery} onChange={(e) => setMemberQuery(e.target.value)}
                placeholder="Search accounts by name or email…"
                className="rounded-full mb-4 sm:max-w-md" data-testid="members-search" />
              <p className="text-xs text-slate-400 font-body mb-3" data-testid="members-count">
                {members.filter((m) => {
                  const q = memberQuery.toLowerCase();
                  return !q || (m.name || "").toLowerCase().includes(q) || (m.email || "").toLowerCase().includes(q);
                }).length} of {members.length} accounts
              </p>

              <div className="space-y-3">
                {members.length === 0 ? (
                  <div className="text-center py-12 bg-white dark:bg-[#1a1b23] rounded-xl border border-slate-100 dark:border-slate-800" data-testid="no-members-state">
                    <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 font-body">No members yet. Click "New Member" to create one.</p>
                  </div>
                ) : members.filter((m) => {
                  const q = memberQuery.toLowerCase();
                  return !q || (m.name || "").toLowerCase().includes(q) || (m.email || "").toLowerCase().includes(q);
                }).map((m) => (
                  <div key={m.id} className="bg-white dark:bg-[#1a1b23] rounded-xl border border-slate-100 dark:border-slate-800 p-5" data-testid={`member-card-${m.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-heading font-semibold text-slate-900 dark:text-white">{m.name}</h3>
                          {!m.active && <Badge className="bg-slate-200 text-slate-600 font-body text-xs rounded-full">Inactive</Badge>}
                          <Badge className="bg-brand-gold/10 text-brand-gold font-body text-xs rounded-full">{m.credits_remaining} credit{m.credits_remaining === 1 ? "" : "s"}</Badge>
                          {m.source === "self_signup" && <Badge className="bg-blue-100 text-blue-700 font-body text-xs rounded-full">Self-signup</Badge>}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 font-body">
                          <a href={`mailto:${m.email}`} className="text-brand-purple hover:underline">{m.email}</a>
                          <span>Created {new Date(m.created_at).toLocaleDateString("en-AU", { dateStyle: "medium" })}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm" variant="outline" className="rounded-full font-body text-xs" data-testid={`add-credits-${m.id}`}
                          onClick={async () => {
                            const v = window.prompt(`How many credits for ${m.name}? (current total: ${m.credits_remaining})\nUse a negative number to remove.`, "10");
                            if (v === null) return;
                            const n = parseInt(v, 10); if (isNaN(n)) return toast.error("Invalid number");
                            await axios.post(`${API}/admin/members/${m.id}/add-credits`, { amount: n }, getAuth());
                            toast.success(n > 0 ? `Added ${n} credits` : `Removed ${Math.abs(n)} credits`); fetchData();
                          }}>
                          Add credits
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full font-body text-xs" data-testid={`reset-pw-${m.id}`}
                          onClick={async () => {
                            const pw = window.prompt(`Set a new password for ${m.name}`, "");
                            if (!pw) return;
                            try {
                              await axios.post(`${API}/admin/members/${m.id}/reset-password`, { password: pw }, getAuth());
                              toast.success("Password reset");
                            } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                          }}>
                          Reset password
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full font-body text-xs" data-testid={`toggle-active-${m.id}`}
                          onClick={async () => {
                            await axios.put(`${API}/admin/members/${m.id}`, { active: !m.active }, getAuth());
                            toast.success(m.active ? "Deactivated" : "Activated"); fetchData();
                          }}>
                          {m.active ? "Deactivate" : "Activate"}
                        </Button>
                        <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full" data-testid={`delete-member-${m.id}`}
                          onClick={() => askConfirm(
                            `Delete ${m.name}?`,
                            `${m.email} — their credits and booking history go with them. This can't be undone.`,
                            async () => {
                              await axios.delete(`${API}/admin/members/${m.id}`, getAuth());
                              toast.success("Member deleted"); fetchData();
                            })}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Orders Tab */}
            <TabsContent value="orders" data-testid="orders-tab-content">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">Orders & purchases</h2>
                <Input value={orderQuery} onChange={(e) => setOrderQuery(e.target.value)}
                  placeholder="Search by email, package or session id…"
                  className="rounded-full sm:max-w-xs" data-testid="orders-search" />
              </div>

              <div className="space-y-3">
                {orders.filter((o) => {
                  const q = orderQuery.toLowerCase();
                  return !q || [o.member_email, o.member_name, o.package_id, o.label, o.session_id]
                    .some((f) => (f || "").toLowerCase().includes(q));
                }).map((o) => (
                  <div key={o.id} className="bg-white dark:bg-[#1a1b23] rounded-xl border border-slate-100 dark:border-slate-800 p-5 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`order-row-${o.id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-heading font-semibold text-slate-900 dark:text-white">
                          ${(o.amount || 0).toFixed(2)} {(o.currency || "aud").toUpperCase()}
                        </h3>
                        <Badge className={`font-body text-xs rounded-full ${
                          o.payment_status === "paid" ? "bg-green-100 text-green-700"
                          : o.payment_status === "refunded" ? "bg-red-100 text-red-600"
                          : "bg-slate-100 text-slate-600"}`} data-testid={`order-status-${o.id}`}>
                          {o.payment_status}
                        </Badge>
                        {o.type === "weekly_subscription" && <Badge className="bg-brand-purple-soft text-brand-purple font-body text-xs rounded-full">SUBSCRIPTION</Badge>}
                        {o.credits ? <Badge className="bg-brand-gold/10 text-brand-gold font-body text-xs rounded-full">{o.credits} credit{o.credits === 1 ? "" : "s"}</Badge> : null}
                      </div>
                      <p className="text-sm text-slate-600 dark:text-slate-300 font-body">
                        {o.member_name ? `${o.member_name} · ` : ""}{o.member_email}
                      </p>
                      <p className="text-xs text-slate-400 font-body">
                        {o.label || o.package_id} · {o.created_at ? new Date(o.created_at).toLocaleString("en-AU") : ""}
                      </p>
                      {o.refunded && <p className="text-xs text-red-500 font-body mt-1">Refunded {o.refunded_at ? new Date(o.refunded_at).toLocaleDateString("en-AU") : ""} by {o.refunded_by}</p>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {!o.refunded && ["paid", "succeeded", "complete"].includes(o.payment_status) && (
                        <Button size="sm" variant="outline" onClick={() => refundOrder(o)}
                          className="rounded-full font-body text-xs border-red-300 text-red-500 hover:bg-red-50"
                          data-testid={`refund-order-${o.id}`}>
                          Refund
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {orders.length === 0 && (
                  <div className="text-center py-12 bg-white dark:bg-[#1a1b23] rounded-xl border border-slate-100 dark:border-slate-800" data-testid="no-orders-state">
                    <p className="text-slate-500 font-body">No purchases yet.</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Blog Tab */}
            <TabsContent value="blog" data-testid="blog-tab-content">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">Blog posts</h2>
                <Button onClick={() => { setEditingPost(null); setPostDialog(true); }} className="bg-brand-purple hover:bg-brand-purple-hover text-white rounded-full font-body" data-testid="add-post-btn">
                  <Plus className="h-4 w-4 mr-1" /> Write a post
                </Button>
              </div>

              <div className="bg-white dark:bg-[#1a1b23] rounded-xl border border-slate-100 dark:border-slate-800 p-5 mb-5">
                <h3 className="font-heading text-sm font-semibold text-slate-900 dark:text-white mb-1">Import from Substack</h3>
                <p className="text-xs text-slate-500 font-body mb-3">Paste your Substack address (e.g. tutorsmelbourne.substack.com) and we'll pull in every post. Already-imported posts are skipped.</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input value={substackUrl} onChange={(e) => setSubstackUrl(e.target.value)} placeholder="tutorsmelbourne.substack.com" className="rounded-xl" data-testid="substack-url-input" />
                  <Button
                    disabled={importing || !substackUrl.trim()}
                    onClick={async () => {
                      setImporting(true);
                      try {
                        const res = await axios.post(`${API}/admin/blog/import-substack`, { feed_url: substackUrl.trim(), publish: true }, getAuth());
                        toast.success(`Imported ${res.data.imported} post(s), skipped ${res.data.skipped} already saved`);
                        await reloadBlog();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Import failed");
                      } finally { setImporting(false); }
                    }}
                    className="bg-brand-gold hover:bg-brand-gold-hover text-white rounded-full font-body whitespace-nowrap"
                    data-testid="substack-import-btn">
                    <Download className="h-4 w-4 mr-1" /> {importing ? "Importing…" : "Import posts"}
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                {posts.length === 0 ? (
                  <div className="text-center py-12 bg-white dark:bg-[#1a1b23] rounded-xl border border-slate-100 dark:border-slate-800" data-testid="no-posts-state">
                    <PenLine className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 font-body">No posts yet. Write one or import from Substack.</p>
                  </div>
                ) : posts.map((p) => (
                  <div key={p.id} className="bg-white dark:bg-[#1a1b23] rounded-xl border border-slate-100 dark:border-slate-800 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`post-row-${p.slug}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-heading font-semibold text-slate-900 dark:text-white">{p.title}</h3>
                        {!p.published && <Badge className="bg-slate-200 text-slate-600 font-body text-xs rounded-full">Draft</Badge>}
                        {p.source === "substack" && <Badge className="bg-blue-100 text-blue-700 font-body text-xs rounded-full">Substack</Badge>}
                        {p.category && <Badge className="bg-brand-gold/10 text-brand-gold font-body text-xs rounded-full">{p.category}</Badge>}
                      </div>
                      <p className="text-xs text-slate-500 font-body truncate">/blog/{p.slug}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="rounded-full font-body text-xs" data-testid={`edit-post-${p.slug}`}
                        onClick={async () => {
                          const full = await axios.get(`${API}/blog/${p.slug}`);
                          setEditingPost(full.data); setPostDialog(true);
                        }}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                      </Button>
                      <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full" data-testid={`delete-post-${p.slug}`}
                        onClick={() => askConfirm(
                          `Delete "${p.title}"?`,
                          "The post is removed from your blog and from search results over time.",
                          async () => {
                            await axios.delete(`${API}/admin/blog/${p.id}`, getAuth());
                            toast.success("Post deleted"); reloadBlog();
                          })}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings" data-testid="settings-tab-content">
              <div className="bg-white dark:bg-[#1a1b23] rounded-xl border border-slate-100 dark:border-slate-800 p-6 max-w-2xl">
                <div className="flex items-center gap-2 mb-1">
                  <SettingsIcon className="h-5 w-5 text-brand-purple" />
                  <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">Site settings</h2>
                </div>
                <p className="text-xs text-slate-500 font-body mb-6">
                  The term label is used in every "Enrol For …" button, the nav bar, the promo bar and the enrolment page.
                  Change it here at the start of each term — no developer needed.
                </p>

                <div className="space-y-4">
                  <div>
                    <Label className="font-body text-sm">Current term label</Label>
                    <div className="flex flex-col sm:flex-row gap-2 mt-1.5">
                      <Input value={termInput || settings.term_label} onChange={(e) => setTermInput(e.target.value)} placeholder="Term 4" className="rounded-xl" data-testid="term-label-input" />
                      <Button onClick={() => saveSettings({ term_label: termInput })} className="bg-brand-purple hover:bg-brand-purple-hover text-white rounded-full font-body whitespace-nowrap" data-testid="save-term-btn">
                        Save
                      </Button>
                    </div>
                    <p className="text-xs text-slate-400 font-body mt-2">Currently live: <strong>{settings.term_label}</strong> · e.g. "Term 1 2027"</p>
                  </div>

                  <div>
                    <Label className="font-body text-sm">Enrolment page sub-headline</Label>
                    <Textarea
                      defaultValue={settings.enrol_headline}
                      onBlur={(e) => e.target.value !== settings.enrol_headline && saveSettings({ enrol_headline: e.target.value })}
                      rows={2} className="mt-1.5 rounded-xl" data-testid="enrol-headline-input" />
                  </div>

                  <div>
                    <Label className="font-body text-sm">Social-proof line under the enrolment heading</Label>
                    <Input
                      defaultValue={settings.spots_note}
                      onBlur={(e) => e.target.value !== settings.spots_note && saveSettings({ spots_note: e.target.value })}
                      className="mt-1.5 rounded-xl" data-testid="spots-note-input" />
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Action confirmation (replaces the browser's native confirm box) */}
      <Dialog open={!!confirmAsk} onOpenChange={(o) => !o && !confirmBusy && setConfirmAsk(null)}>
        <DialogContent className="max-w-md bg-white dark:bg-[#1a1b23]" data-testid="confirm-dialog">
          <DialogHeader>
            <div className="w-11 h-11 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center mb-2">
              <Trash2 className="h-5 w-5 text-red-500" />
            </div>
            <DialogTitle className="font-heading text-xl text-slate-900 dark:text-white" data-testid="confirm-dialog-title">
              {confirmAsk?.title}
            </DialogTitle>
            <DialogDescription className="font-body text-slate-500 leading-relaxed">
              {confirmAsk?.description}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setConfirmAsk(null)} disabled={confirmBusy}
              className="rounded-full font-body" data-testid="confirm-cancel-btn">
              Cancel
            </Button>
            <Button onClick={runConfirm} disabled={confirmBusy}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full font-body" data-testid="confirm-accept-btn">
              {confirmBusy ? "Working…" : confirmAsk?.actionLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Refund confirmation */}
      <Dialog open={!!refundTarget} onOpenChange={(o) => !o && setRefundTarget(null)}>
        <DialogContent className="max-w-md bg-white dark:bg-[#1a1b23]" data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-slate-900 dark:text-white">Refund this purchase?</DialogTitle>
            <DialogDescription className="font-body text-slate-500">
              {refundTarget ? `$${Number(refundTarget.amount || 0).toFixed(2)} will be refunded to ${refundTarget.member_email} via Stripe.` : ""}
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-3 rounded-xl border border-slate-200 dark:border-slate-700 p-3 text-sm font-body text-slate-700 dark:text-slate-200 cursor-pointer">
            <input type="checkbox" checked={reclaimCredits} onChange={(e) => setReclaimCredits(e.target.checked)}
              className="mt-1" data-testid="refund-reclaim-checkbox" />
            <span>
              Also remove the {refundTarget?.credits || 0} credit(s) this purchase granted
              <span className="block text-xs text-slate-500 mt-0.5">Untick to refund the money but let them keep the credits.</span>
            </span>
          </label>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setRefundTarget(null)} className="rounded-full font-body" data-testid="refund-cancel-btn">Cancel</Button>
            <Button onClick={confirmRefund} disabled={refunding}
              className="bg-red-600 hover:bg-red-700 text-white rounded-full font-body" data-testid="refund-confirm-btn">
              {refunding ? "Refunding…" : "Refund now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Member Dialog */}
      <MemberFormDialog open={memberDialog} onClose={() => setMemberDialog(false)} onSave={fetchData} />

      {/* Blog post editor */}
      <BlogPostDialog open={postDialog} onClose={() => setPostDialog(false)} post={editingPost} onSave={reloadBlog} />

      {/* Class Dialog */}
      <ClassFormDialog open={classDialog} onClose={() => setClassDialog(false)} editingClass={editingClass} subjects={subjects} onSave={fetchData} />

      {/* Subject Dialog */}
      <SubjectFormDialog open={subjectDialog} onClose={() => setSubjectDialog(false)} onSave={fetchData} />

      {/* Bookings Dialog */}
      <Dialog open={!!bookingsDialog} onOpenChange={() => setBookingsDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Students - {bookingsDialog?.title}</DialogTitle>
            <DialogDescription className="font-body text-sm text-slate-500">Paid students enrolled in this class</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {classBookings.length === 0 ? (
              <p className="text-sm text-slate-500 font-body py-4 text-center">No paid bookings for this class</p>
            ) : classBookings.map((b) => (
              <div key={b.id} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-body font-semibold text-sm text-slate-900">{b.student_name}</p>
                  <p className="text-xs text-slate-500 font-body">{b.student_email}</p>
                </div>
                {b.phone && <span className="text-xs text-slate-400 font-body">{b.phone}</span>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AdminClassCard({ cls, onEdit, onDelete, onViewBookings }) {
  return (
    <div className="bg-white dark:bg-[#1a1b23] rounded-xl border border-slate-100 dark:border-slate-800 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-colors duration-300" data-testid={`admin-class-${cls.id}`}>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <Badge className="bg-brand-purple-soft dark:bg-brand-purple/20 text-brand-purple dark:text-brand-purple-light font-body text-xs rounded-full">{cls.subject_name}</Badge>
          <Badge variant="outline" className={`text-xs rounded-full font-body ${cls.status === "active" ? "text-green-600 border-green-200" : "text-red-500 border-red-200"}`}>{cls.status}</Badge>
          {cls.year_level && <Badge variant="outline" className="text-xs rounded-full font-body text-slate-500">{cls.year_level}</Badge>}
        </div>
        <h3 className="font-heading font-semibold text-slate-900 dark:text-white">{cls.title}</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 font-body">{cls.date} | {cls.start_time}-{cls.end_time} | {cls.tutor_name} | ${cls.price?.toFixed(2)} AUD</p>
        <p className="text-xs text-slate-400 dark:text-slate-500 font-body mt-1">Occupancy: {cls.current_occupancy}/{cls.max_occupancy}</p>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onViewBookings} className="rounded-full font-body text-xs" data-testid={`view-bookings-${cls.id}`}>
          <Users className="h-3 w-3 mr-1" /> Students
        </Button>
        <Button variant="outline" size="icon" onClick={onEdit} className="rounded-full" data-testid={`edit-class-${cls.id}`}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="icon" onClick={onDelete} className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded-full" data-testid={`delete-class-${cls.id}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function ClassFormDialog({ open, onClose, editingClass, subjects, onSave }) {
  const blank = { subject_id: "", title: "", description: "", tutor_name: "", tutor_email: "", date: "", start_time: "", end_time: "", max_occupancy: 10, price: 50.0, google_meet_link: "", year_level: "", repeat_weekly: false, repeat_weeks: 8 };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingClass) {
      setForm({ subject_id: editingClass.subject_id || "", title: editingClass.title || "", description: editingClass.description || "",
        tutor_name: editingClass.tutor_name || "", tutor_email: editingClass.tutor_email || "", date: editingClass.date || "",
        start_time: editingClass.start_time || "", end_time: editingClass.end_time || "", max_occupancy: editingClass.max_occupancy || 10,
        price: editingClass.price || 50.0, google_meet_link: editingClass.google_meet_link || "", year_level: editingClass.year_level || "",
        repeat_weekly: false, repeat_weeks: 8 });
    } else { setForm(blank); }
  }, [editingClass, open]);

  const handleSave = async () => {
    if (!form.subject_id || !form.title || !form.date || !form.start_time || !form.end_time || !form.tutor_name || !form.tutor_email) {
      toast.error("Please fill all required fields"); return;
    }
    setSaving(true);
    try {
      const auth = getAuth();
      if (editingClass) {
        await axios.put(`${API}/classes/${editingClass.id}`, { ...form, price: parseFloat(form.price), max_occupancy: parseInt(form.max_occupancy) }, auth);
        toast.success("Class updated");
      } else {
        const payload = {
          ...form,
          price: parseFloat(form.price),
          max_occupancy: parseInt(form.max_occupancy),
          repeat_weeks: parseInt(form.repeat_weeks) || 1,
        };
        const res = await axios.post(`${API}/classes`, payload, auth);
        if (res.data.count) {
          toast.success(`${res.data.count} weekly classes created`);
        } else {
          toast.success("Class created");
        }
      }
      onSave(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const update = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value || e }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="class-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{editingClass ? "Edit Class" : "Add New Class"}</DialogTitle>
          <DialogDescription className="font-body text-sm text-slate-500">{editingClass ? "Update class details below" : "Fill in the details to create a new class"}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="font-body text-sm mb-1.5 block">Subject *</Label>
            <Select value={form.subject_id} onValueChange={(v) => setForm(p => ({ ...p, subject_id: v }))}>
              <SelectTrigger className="rounded-lg font-body" data-testid="select-subject"><SelectValue placeholder="Select subject" /></SelectTrigger>
              <SelectContent>
                {subjects.map(s => <SelectItem key={s.id} value={s.id} className="font-body">{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-body text-sm mb-1.5 block">Title *</Label>
            <Input value={form.title} onChange={update("title")} placeholder="e.g. Year 10 Maths Group" className="rounded-lg font-body" data-testid="input-class-title" />
          </div>
          <div className="sm:col-span-2">
            <Label className="font-body text-sm mb-1.5 block">Description</Label>
            <Textarea value={form.description} onChange={update("description")} placeholder="Class description..." className="rounded-lg font-body" rows={2} data-testid="input-class-desc" />
          </div>
          <div>
            <Label className="font-body text-sm mb-1.5 block">Tutor Name *</Label>
            <Input value={form.tutor_name} onChange={update("tutor_name")} placeholder="Tutor name" className="rounded-lg font-body" data-testid="input-tutor-name" />
          </div>
          <div>
            <Label className="font-body text-sm mb-1.5 block">Tutor Email *</Label>
            <Input type="email" value={form.tutor_email} onChange={update("tutor_email")} placeholder="tutor@email.com" className="rounded-lg font-body" data-testid="input-tutor-email" />
          </div>
          <div>
            <Label className="font-body text-sm mb-1.5 block">Date *</Label>
            <Input type="date" value={form.date} onChange={update("date")} className="rounded-lg font-body" data-testid="input-class-date" />
          </div>
          <div>
            <Label className="font-body text-sm mb-1.5 block">Year Level</Label>
            <Input value={form.year_level} onChange={update("year_level")} placeholder="e.g. Year 10-12" className="rounded-lg font-body" data-testid="input-year-level" />
          </div>
          <div>
            <Label className="font-body text-sm mb-1.5 block">Start Time *</Label>
            <Input type="time" value={form.start_time} onChange={update("start_time")} className="rounded-lg font-body" data-testid="input-start-time" />
          </div>
          <div>
            <Label className="font-body text-sm mb-1.5 block">End Time *</Label>
            <Input type="time" value={form.end_time} onChange={update("end_time")} className="rounded-lg font-body" data-testid="input-end-time" />
          </div>
          <div>
            <Label className="font-body text-sm mb-1.5 block">Max Occupancy</Label>
            <Input type="number" value={form.max_occupancy} onChange={update("max_occupancy")} min={1} className="rounded-lg font-body" data-testid="input-max-occupancy" />
          </div>
          <div>
            <Label className="font-body text-sm mb-1.5 block">Price (AUD)</Label>
            <Input type="number" step="0.01" value={form.price} onChange={update("price")} min={0} className="rounded-lg font-body" data-testid="input-price" />
          </div>
          <div className="sm:col-span-2">
            <Label className="font-body text-sm mb-1.5 block">Google Meet Link</Label>
            <Input value={form.google_meet_link} onChange={update("google_meet_link")} placeholder="https://meet.google.com/xxx-xxx-xxx" className="rounded-lg font-body" data-testid="input-meet-link" />
          </div>
          {!editingClass && (
            <div className="sm:col-span-2 bg-brand-purple-soft/40 dark:bg-brand-purple/10 rounded-xl p-4 border border-brand-purple/20" data-testid="repeat-weekly-block">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.repeat_weekly}
                  onChange={(e) => setForm((p) => ({ ...p, repeat_weekly: e.target.checked }))}
                  className="mt-1 h-4 w-4 rounded accent-brand-purple"
                  data-testid="input-repeat-weekly"
                />
                <div className="flex-1">
                  <div className="font-body font-semibold text-slate-900 dark:text-white text-sm">Repeat weekly</div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-body">Create multiple weekly occurrences of this class automatically.</p>
                  {form.repeat_weekly && (
                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-sm font-body text-slate-600 dark:text-slate-300">for</span>
                      <Input
                        type="number"
                        min={2}
                        max={52}
                        value={form.repeat_weeks}
                        onChange={update("repeat_weeks")}
                        className="w-20 rounded-lg font-body"
                        data-testid="input-repeat-weeks"
                      />
                      <span className="text-sm font-body text-slate-600 dark:text-slate-300">weeks (total occurrences)</span>
                    </div>
                  )}
                </div>
              </label>
            </div>
          )}
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-full font-body">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-brand-purple hover:bg-brand-purple-hover text-white rounded-full font-body" data-testid="save-class-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingClass ? "Update Class" : "Create Class"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SubjectFormDialog({ open, onClose, onSave }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast.error("Subject name required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/subjects`, { name, description: desc }, getAuth());
      toast.success("Subject created");
      setName(""); setDesc(""); onSave(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create subject");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="subject-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Add Subject</DialogTitle>
          <DialogDescription className="font-body text-sm text-slate-500">Create a new subject category for classes</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="font-body text-sm mb-1.5 block">Subject Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. English" className="rounded-lg font-body" data-testid="input-subject-name" />
          </div>
          <div>
            <Label className="font-body text-sm mb-1.5 block">Description</Label>
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Brief description..." className="rounded-lg font-body" rows={2} data-testid="input-subject-desc" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full font-body">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-brand-purple hover:bg-brand-purple-hover text-white rounded-full font-body" data-testid="save-subject-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Subject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MemberFormDialog({ open, onClose, onSave }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", credits: 0 });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm({ name: "", email: "", password: "", credits: 0 }); }, [open]);

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) {
      toast.error("Name, email and a password (6+ chars) are required.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/members`, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        credits: parseInt(form.credits, 10) || 0,
      }, getAuth());
      toast.success("Member created — share their login + password");
      onSave(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create member");
    } finally { setSaving(false); }
  };

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="member-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Create New Member</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="font-body text-sm mb-1.5 block">Full Name *</Label>
            <Input value={form.name} onChange={update("name")} placeholder="Student's full name" className="rounded-lg font-body" data-testid="member-input-name" />
          </div>
          <div>
            <Label className="font-body text-sm mb-1.5 block">Email *</Label>
            <Input type="email" value={form.email} onChange={update("email")} placeholder="student@example.com" className="rounded-lg font-body" data-testid="member-input-email-admin" />
          </div>
          <div>
            <Label className="font-body text-sm mb-1.5 block">Initial Password *</Label>
            <Input type="text" value={form.password} onChange={update("password")} placeholder="At least 6 characters — share this with the student" className="rounded-lg font-body" data-testid="member-input-password-admin" />
            <p className="text-xs text-slate-400 mt-1 font-body">They can change it later if needed via reset.</p>
          </div>
          <div>
            <Label className="font-body text-sm mb-1.5 block">Starting Credits</Label>
            <Input type="number" value={form.credits} onChange={update("credits")} min={0} max={500} className="rounded-lg font-body" data-testid="member-input-credits" />
            <p className="text-xs text-slate-400 mt-1 font-body">Credits expire after 10 weeks of no usage.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-full font-body" data-testid="member-cancel-btn">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-brand-purple hover:bg-brand-purple-hover text-white rounded-full font-body" data-testid="member-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Member"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function BlogPostDialog({ open, onClose, post, onSave }) {
  const blank = { title: "", excerpt: "", content: "", cover_image: "", category: "VCE Tips", author: "Yahav Ben Yosef", published: true, external_url: "" };
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm(post ? { ...blank, ...post } : blank);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, post]);

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.title.trim()) return toast.error("A title is required");
    setSaving(true);
    try {
      const payload = {
        title: form.title, excerpt: form.excerpt, content: form.content,
        cover_image: form.cover_image, category: form.category,
        author: form.author, published: !!form.published, external_url: form.external_url || "",
      };
      if (post?.id) {
        await axios.put(`${API}/admin/blog/${post.id}`, payload, getAuth());
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/admin/blog`, payload, getAuth());
        toast.success("Post published");
      }
      onSave(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save post");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-[#1a1b23]" data-testid="blog-post-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-slate-900 dark:text-white">{post ? "Edit post" : "Write a post"}</DialogTitle>
          <DialogDescription className="font-body text-slate-500">Plain text or HTML both work in the body.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <Label className="font-body text-sm">Title *</Label>
            <Input value={form.title} onChange={update("title")} className="mt-1.5 rounded-xl" data-testid="post-title-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="font-body text-sm">Category</Label>
              <Input value={form.category} onChange={update("category")} className="mt-1.5 rounded-xl" data-testid="post-category-input" />
            </div>
            <div>
              <Label className="font-body text-sm">Author</Label>
              <Input value={form.author} onChange={update("author")} className="mt-1.5 rounded-xl" data-testid="post-author-input" />
            </div>
          </div>
          <div>
            <Label className="font-body text-sm">Cover image URL</Label>
            <Input value={form.cover_image} onChange={update("cover_image")} placeholder="https://…" className="mt-1.5 rounded-xl" data-testid="post-cover-input" />
          </div>
          <div>
            <Label className="font-body text-sm">Short summary</Label>
            <Textarea value={form.excerpt} onChange={update("excerpt")} rows={2} className="mt-1.5 rounded-xl" data-testid="post-excerpt-input" />
          </div>
          <div>
            <Label className="font-body text-sm">Body</Label>
            <Textarea value={form.content} onChange={update("content")} rows={12} className="mt-1.5 rounded-xl font-mono text-xs" data-testid="post-content-input" />
          </div>
          <label className="flex items-center gap-2 text-sm font-body text-slate-700 dark:text-slate-200">
            <input type="checkbox" checked={!!form.published} onChange={(e) => setForm((p) => ({ ...p, published: e.target.checked }))} data-testid="post-published-checkbox" />
            Published (visible on the public blog)
          </label>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-full font-body">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-brand-purple hover:bg-brand-purple-hover text-white rounded-full font-body" data-testid="post-save-btn">
            {saving ? "Saving…" : post ? "Save changes" : "Publish post"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
